import { getBankName } from "@/utils/banks";
import { formatBtgDateTime } from "@/utils/btg";
import { Mask } from "@/utils/mask";

export interface PixReceiptData {
  uuid: string;
  pix_key?: string | null;
  btg_receiver_name?: string | null;
  btg_receiver_tax_id?: string | null;
  btg_receiver_bank_code?: string | null;
  btg_end_to_end_id?: string | null;
  btg_payload?: {
    amount?: number;
    paymentDate?: string;
    debitParty?: {
      name?: string;
      taxId?: string;
      bankCode?: string;
      branch?: string;
      branchCode?: string;
      number?: string;
    };
  } | null;
}

type Row = [label: string, value: string];

const WIDTH = 720;
const PADDING = 40;
const SCALE = 2;
const FONT = "-apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const MONO = "ui-monospace, Menlo, Consolas, monospace";

/** Quebra o texto em linhas que caibam em maxWidth (chaves/IDs longos quebram por caractere). */
const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
  const lines: string[] = [];
  let current = "";
  for (const char of text) {
    if (ctx.measureText(current + char).width > maxWidth && current) {
      lines.push(current);
      current = char;
    } else {
      current += char;
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [""];
};

/** Monta as seções do comprovante (sem o aviso de titular divergente, de uso interno). */
const buildSections = (data: PixReceiptData) => {
  const payload = data.btg_payload;
  const debit = payload?.debitParty;
  const dateTime = formatBtgDateTime(payload?.paymentDate, data.btg_end_to_end_id);

  const receiver: Row[] = [
    ["Documento", data.btg_receiver_tax_id ?? "-"],
    ["Banco", getBankName(data.btg_receiver_bank_code)],
    ["Chave", data.pix_key ?? "-"],
  ];

  const origin: Row[] = debit
    ? [
        ["Documento", debit.taxId ?? "-"],
        ["Banco", getBankName(debit.bankCode)],
        ["Agência/Conta", `${debit.branchCode ?? debit.branch ?? "-"} / ${debit.number ?? "-"}`],
      ]
    : [];

  return {
    amount: payload?.amount != null ? Mask.formatCurrency(payload.amount) : null,
    dateTime,
    receiverName: data.btg_receiver_name ?? "-",
    receiver,
    originName: debit?.name ?? null,
    origin,
  };
};

export const downloadPixReceipt = (data: PixReceiptData) => {
  const s = buildSections(data);
  const innerWidth = WIDTH - PADDING * 2;
  const labelWidth = 130;
  const valueWidth = innerWidth - 32 - labelWidth;

  const measure = document.createElement("canvas").getContext("2d");
  if (!measure) return;
  measure.font = `15px ${FONT}`;

  // Altura de cada bloco (nome + linhas, com quebra de valores longos)
  const blockHeight = (rows: Row[]) =>
    rows.reduce((total, [, value]) => total + wrapText(measure, value, valueWidth).length * 22, 0);

  const receiverH = 28 + 34 + blockHeight(s.receiver) + 20;
  const originH = s.origin.length ? 28 + 34 + blockHeight(s.origin) + 20 : 0;
  const e2eLines = data.btg_end_to_end_id
    ? wrapText(measure, data.btg_end_to_end_id, innerWidth).length
    : 0;

  const headerH = 150;
  const height =
    headerH + receiverH + 20 + (originH ? originH + 20 : 0) + (e2eLines ? 36 + e2eLines * 18 : 0) + 60;

  const canvas = document.createElement("canvas");
  canvas.width = WIDTH * SCALE;
  canvas.height = height * SCALE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, WIDTH, height);

  // Cabeçalho
  ctx.fillStyle = "#15803d";
  ctx.fillRect(0, 0, WIDTH, 8);
  ctx.textBaseline = "alphabetic";
  ctx.fillStyle = "#15803d";
  ctx.font = `bold 26px ${FONT}`;
  ctx.fillText("PIX enviado", PADDING, 62);
  ctx.fillStyle = "#6b7280";
  ctx.font = `14px ${FONT}`;
  ctx.fillText("Comprovante de devolução", PADDING, 86);

  if (s.amount) {
    ctx.fillStyle = "#111827";
    ctx.font = `bold 28px ${FONT}`;
    ctx.textAlign = "right";
    ctx.fillText(s.amount, WIDTH - PADDING, 62);
    ctx.textAlign = "left";
  }
  if (s.dateTime) {
    ctx.fillStyle = "#6b7280";
    ctx.font = `14px ${FONT}`;
    ctx.textAlign = "right";
    ctx.fillText(s.dateTime, WIDTH - PADDING, 86);
    ctx.textAlign = "left";
  }

  ctx.strokeStyle = "#e5e7eb";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(PADDING, 112);
  ctx.lineTo(WIDTH - PADDING, 112);
  ctx.stroke();

  const drawBlock = (
    y: number,
    title: string,
    name: string,
    rows: Row[],
    accent: string,
    background: string,
    nameColor: string
  ) => {
    const h = 28 + 34 + blockHeight(rows) + 20;
    ctx.fillStyle = background;
    ctx.fillRect(PADDING, y, innerWidth, h);
    ctx.fillStyle = accent;
    ctx.fillRect(PADDING, y, 5, h);

    ctx.fillStyle = "#6b7280";
    ctx.font = `bold 12px ${FONT}`;
    ctx.fillText(title.toUpperCase(), PADDING + 20, y + 28);
    ctx.fillStyle = nameColor;
    ctx.font = `bold 18px ${FONT}`;
    ctx.fillText(name.toUpperCase(), PADDING + 20, y + 58);

    let rowY = y + 28 + 34 + 4;
    rows.forEach(([label, value]) => {
      ctx.font = `15px ${FONT}`;
      const lines = wrapText(ctx, value, valueWidth);
      ctx.fillStyle = "#6b7280";
      ctx.fillText(label, PADDING + 20, rowY + 14);
      ctx.fillStyle = "#111827";
      ctx.font = `15px ${MONO}`;
      lines.forEach((line, i) => ctx.fillText(line, PADDING + 20 + labelWidth, rowY + 14 + i * 22));
      rowY += lines.length * 22;
    });
    return y + h;
  };

  let y = headerH - 20;
  y = drawBlock(y, "Quem recebe", s.receiverName, s.receiver, "#2563eb", "#eff6ff", "#1e40af");
  if (s.origin.length) {
    y += 20;
    y = drawBlock(y, "De onde saiu", s.originName ?? "-", s.origin, "#64748b", "#f8fafc", "#111827");
  }

  if (data.btg_end_to_end_id) {
    y += 36;
    ctx.fillStyle = "#6b7280";
    ctx.font = `11px ${FONT}`;
    ctx.fillText("ID DA TRANSAÇÃO (END TO END)", PADDING, y - 8);
    ctx.font = `13px ${MONO}`;
    ctx.fillStyle = "#374151";
    ctx.font = `13px ${MONO}`;
    wrapText(ctx, data.btg_end_to_end_id, innerWidth).forEach((line, i) =>
      ctx.fillText(line, PADDING, y + 10 + i * 18)
    );
  }

  canvas.toBlob((blob) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `comprovante-pix-${data.btg_end_to_end_id ?? data.uuid}.png`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, "image/png");
};
